import type { Address } from "@/services/addresses/addressTypes";
import { deleteCartItem, savePaymentMethod, saveShippingMethod, updateCartItem } from "@/services/marketplace/cart/cartApi";
import type { ShoppingCart_WithSelectedPaymentMethod, ShoppingCart_WithSelectedShippingMethod, ShoppingCart_WithShippingAddress, ShoppingCart, CartItem, SingleSellerCart } from "@/services/marketplace/cart/cartTypes";
import type { PaymentMethod } from "@/services/marketplace/paymentMethod/paymentMethodTypes";
import type { ShippingMethod } from "@/services/marketplace/shippingMethod/shippingMethodTypes";
import { useAnonymousCartPreferences } from "@/composables/marketplace/cart/useAnonymousCartPreferences.client";
import { useUser } from "@/composables/user/user";
import type { UserId } from "@/services/user/userTypes";
import type { Ref } from "vue";

export function assertCartExists(cart: ShoppingCart | undefined): asserts cart is ShoppingCart {
  if (cart === undefined) {
    throw new Error("Cart does not exist.");
  }
}

export function hasSelectedShippingMethod(cart: ShoppingCart | undefined): cart is ShoppingCart_WithSelectedShippingMethod {
  return cart !== undefined && cart.selectedShippingMethod !== null;
}

export function hasSelectedPaymentMethod(cart: ShoppingCart | undefined): cart is ShoppingCart_WithSelectedPaymentMethod {
  return cart !== undefined && cart.selectedPaymentMethod !== null;
}

export function hasShippingAddress(cart: ShoppingCart | undefined): cart is ShoppingCart_WithShippingAddress {
  return cart !== undefined && cart.shippingAddress !== null;
}

export async function setShippingMethod(cart: Ref<ShoppingCart | undefined>, method: ShippingMethod, selectedSellerId?: UserId): Promise<ShoppingCart | undefined> {
  assertCartExists(cart.value);
  if (cart.value.selectedShippingMethod?.id !== method.id) {
    const { isUserLoggedIn } = useUser();
    if (!isUserLoggedIn.value) {
      useAnonymousCartPreferences().setShippingMethod(method);
      cart.value = { ...cart.value, selectedShippingMethod: method };
      return cart.value;
    }
    return saveShippingMethod(method.id, selectedSellerId); // cart should be updated inside this method
  }
}

export async function setPaymentMethod(cart: Ref<ShoppingCart | undefined>, method: PaymentMethod, selectedSellerId?: UserId): Promise<ShoppingCart | undefined> {
  assertCartExists(cart.value);
  if (cart.value.selectedPaymentMethod?.id !== method.id) {
    const { isUserLoggedIn } = useUser();
    if (!isUserLoggedIn.value) {
      useAnonymousCartPreferences().setPaymentMethod(method);
      cart.value = { ...cart.value, selectedPaymentMethod: method };
      return cart.value;
    }
    return savePaymentMethod(method.id, selectedSellerId); // cart should be updated inside this method
  }
}

export function setShippingAddress(cart: Ref<ShoppingCart | undefined>, address: Address): void {
  assertCartExists(cart.value);
  cart.value.shippingAddress = address;
}

export async function updateCartItemQuantity(cart: Ref<ShoppingCart | undefined>, item: CartItem, quantity: number, anonymousIdentifier?: string): Promise<ShoppingCart | undefined> {
  if (quantity <= 0) {
    return deleteCartItem(item.id, anonymousIdentifier);
  } else {
    assertCartExists(cart.value);

    const originalItem = cart.value.subcarts.flatMap((sellerCart) => sellerCart.items).find((sellerCartItem) => sellerCartItem.id === item.id);
    if (originalItem === undefined) {
      throw new Error("Item not found in cart. Not able to update quantity. Item id: " + item.id);
    }

    const originalQuantity = originalItem.quantity;
    const diff = quantity - originalQuantity;
    return updateCartItem(item.id, diff, anonymousIdentifier);
  }
}

export function getSellerCart(cart: Ref<ShoppingCart | undefined>, sellerNickname: string): SingleSellerCart | undefined {
  assertCartExists(cart.value);
  return cart.value.subcarts.find(({ sellerNickname: cartSellerNickname }) => cartSellerNickname === sellerNickname);
}
